import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { addDays, deliverableFormatLabel, priceBasisText } from "@talentkontor/domain";
import { useStaffWorkspace } from "../../../app/session";
import { api } from "../../../data";
import { ErrorState, Loading, NoAccess } from "../../../components/States";
import { useAction, useRueckfrage } from "../../../components/ui";
import { angebotFertigFrage } from "../../../model/folgen-texte";
import { BetragErkannt, MengeErkannt, betragUnlesbar } from "../../../components/Erkannt";
import { parseQuantityInput } from "../../../lib/betrag";
import { money } from "../../../lib/format";
import { useFormularDaten, useHeute } from "../kooperationen/hooks";
import { katalogAuswahl, platformLabel } from "../kooperationen/model";
import { saveFile } from "../vertraege/ausfertigung";
import { angebotPdf } from "./angebotPdf";
import { schreibeAngebotFest, speichereAngebot, type AnfrageDetail, type AngebotsZeile, type OfferLineRow, type OfferRow } from "./daten";
import { useAnfrage } from "./hooks";
import { TarifHinweis, useModul } from "../../../components/Tarif";
import {
  FORMAT_CHOICES, bpOf, bpText, editorSummen, leereZeile, lineAusZeile, nameOf, preisAusListe, vorbelegung, zeileAusLine, zeilenBetrag,
  type Talentlike, type ZeileEntwurf,
} from "./model";
import "../kooperationen/kooperationen.css";
import "./anfragen.css";
import { t, currentLang } from "../../../lib/sprache";
import { PdfVorschau, type PdfQuelle } from "../../../components/PdfVorschau";

interface Meta {
  language: "de" | "en";
  validUntil: string;
  basis: "net" | "gross";
  commissionIncluded: boolean;
  feePercent: string;
  intro: string;
  terms: string;
}

/**
 * Angebot bearbeiten (0072): Positionen je Creator, vorbelegt aus der Preisliste (Grundpreis je Plattform und Format, Zuschläge
 * nach den Wünschen der Anfrage), jede Zeile änderbar. Zuschläge in Prozent auf den Grundpreis: Nutzungsrechte und Exklusivität
 * je Monat oder pauschal, Eile, zusätzliche Korrekturschleifen. Preisbasis (netto/brutto) und Provision stehen im Angebot.
 */
export function AngebotEditor() {
  const { inquiryId, offerId } = useParams();
  const ws = useStaffWorkspace();
  const q = useAnfrage(inquiryId);
  const fd = useFormularDaten();
  if (!ws.can("view_amounts")) return <NoAccess text={t("Angebote brauchen das Recht „Beträge sehen“.", "Offers require the “View amounts” permission.")} />;
  if (q.error) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  if (fd.error) return <ErrorState error={fd.error} retry={() => void fd.refetch()} />;
  if (q.isPending || !fd.data) return <Loading />;
  if (!q.data) return <NoAccess text={t("Diese Anfrage gibt es nicht oder sie gehört zu einer anderen Agentur.", "This inquiry does not exist or belongs to another agency.")} />;
  const offer = offerId && offerId !== "neu" ? q.data.offers.find((o) => o.id === offerId) : undefined;
  if (offerId && offerId !== "neu" && !offer) return <NoAccess text={t("Dieses Angebot gibt es nicht.", "This offer does not exist.")} />;
  if (offer && offer.status !== "draft") {
    return (
      <main id="main" className="content">
        <div className="panel stack tk-empty" role="alert">
          <h1 className="tk-state-title">{t("Dieses Angebot ist fertiggestellt.", "This offer is finalised.")}</h1>
          <p>{t("Änderungen gehen über eine neue Fassung.", "Changes are made via a new version.")}</p>
          <Link className="btn" to={`/agentur/anfragen/${q.data.inquiry.id}`}>{t("Zur Anfrage", "Go to inquiry")}</Link>
        </div>
      </main>
    );
  }
  return <Editor key={offer?.id ?? "neu"} d={q.data} offer={offer ?? null} talents={fd.data.talents as Talentlike[]} fd={fd.data} />;
}

function Editor({ d, offer, talents, fd }: { d: AnfrageDetail; offer: OfferRow | null; talents: Talentlike[]; fd: NonNullable<ReturnType<typeof useFormularDaten>["data"]> }) {
  const ws = useStaffWorkspace();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const today = useHeute();
  const { busy, error, run } = useAction();
  const rueckfrage = useRueckfrage();
  // Tarifstaffel (0099): ein neues Angebot erst mit „Anfragen und Angebote“; einen bestehenden Entwurf bearbeiten geht immer
  const neuGesperrt = useModul("module_pipeline").gesperrt && !offer;
  const [fehler, setFehler] = useState<string[]>([]);
  const von = params.get("von") ? d.offers.find((o) => o.id === params.get("von")) : undefined;
  const pl = (k: string) => platformLabel(k, fd.platforms);
  const orgFee = fd.settings?.agency_fee_mode === "percent" ? fd.settings.agency_fee_bp : null;

  const [meta, setMeta] = useState<Meta>(() => {
    const base = offer ?? von;
    const brandLang = typeof d.brand?.language === "string" ? d.brand.language : null;
    const country = typeof d.brand?.country === "string" ? d.brand.country : "DE";
    return {
      language: (base?.language ?? (brandLang === "en" || brandLang === "de" ? brandLang : ["DE", "AT", "CH", "LI"].includes(country) ? "de" : "en")) as "de" | "en",
      validUntil: offer?.valid_until ?? addDays(today, d.settings?.valid_days ?? 14),
      basis: base?.prices_basis ?? d.settings?.prices_basis ?? "net",
      commissionIncluded: base?.commission_included ?? d.settings?.commission_included ?? true,
      feePercent: bpText(base ? base.agency_fee_bp : orgFee),
      intro: (base ? base.intro : d.settings?.default_intro) ?? "",
      terms: (base ? base.terms : d.settings?.default_terms) ?? "",
    };
  });
  const [hinweise, setHinweise] = useState<string[]>([]);
  const [zeilen, setZeilen] = useState<ZeileEntwurf[]>(() => {
    const src = offer ?? von;
    if (src) {
      const offen = new Set(d.candidates.filter((c) => c.status === "proposed").map((c) => c.id));
      return d.lines.filter((l) => l.offer_id === src.id && offen.has(l.candidate_id)).sort((a, b) => a.sort_order - b.sort_order).map(zeileAusLine);
    }
    return vorbelegung(d).zeilen;
  });
  useEffect(() => {
    if (!offer && !von) setHinweise(vorbelegung(d).hinweise);
  }, [offer, von, d]);

  const offeneCreator = d.candidates.filter((c) => c.status === "proposed" || zeilen.some((z) => z.candidateId === c.id));
  const plattformen = katalogAuswahl(fd.platforms, ["instagram", "tiktok", "youtube"], zeilen.map((z) => z.platform));
  const feeBp = bpOf(meta.feePercent);
  const summen = editorSummen(zeilen, { commissionIncluded: meta.commissionIncluded, agencyFeeBp: feeBp != null && !Number.isNaN(feeBp) ? feeBp : null });
  const cur = d.inquiry.currency;
  const setZeile = (key: string, patch: Partial<ZeileEntwurf>) => setZeilen((zs) => zs.map((z) => (z.key === key ? { ...z, ...patch } : z)));

  function pruefe(): { lines: AngebotsZeile[]; values: Record<string, unknown> } | null {
    const f: string[] = [];
    const lines: AngebotsZeile[] = [];
    zeilen.forEach((z, i) => {
      const r = lineAusZeile(z, i + 1);
      f.push(...r.fehler);
      if (r.line) lines.push({ ...r.line, sort_order: 100 + i });
    });
    if (!lines.length && !f.length) f.push(t("Bitte mindestens eine Position anlegen.", "Please add at least one item."));
    if (!/^\d{4}-\d{2}-\d{2}$/.test(meta.validUntil)) f.push(t("Bitte ein Gültigkeitsdatum angeben.", "Please enter a validity date."));
    else if (meta.validUntil < today) f.push(t("Das Angebot muss ab heute gültig sein.", "The offer must be valid from today."));
    if (!meta.commissionIncluded && (feeBp === null || Number.isNaN(feeBp) || feeBp > 10000)) f.push(t("Bitte die Agenturgebühr in Prozent angeben (0 bis 100 %).", "Please enter the agency fee in percent (0 to 100 %)."));
    setFehler(f);
    if (f.length) return null;
    return {
      lines,
      values: { language: meta.language, valid_until: meta.validUntil, prices_basis: meta.basis, commission_included: meta.commissionIncluded,
        agency_fee_bp: meta.commissionIncluded ? (feeBp != null && !Number.isNaN(feeBp) ? feeBp : null) : feeBp, intro: meta.intro, terms: meta.terms },
    };
  }

  async function speichern(festschreiben: boolean) {
    const p = pruefe();
    if (!p) return;
    if (festschreiben && !(await rueckfrage.ask(angebotFertigFrage()))) return;
    const r = await run("speichern", async () => {
      const saved = await speichereAngebot(api, offer?.id ?? null, d.inquiry.id, p.values, p.lines);
      if (festschreiben) await schreibeAngebotFest(api, saved.offer_id);
      return saved;
    }, festschreiben ? t("Angebot fertiggestellt.", "Offer finalised.") : t("Entwurf gespeichert.", "Draft saved."));
    if (!r) return;
    if (festschreiben) navigate(`/agentur/anfragen/${d.inquiry.id}?senden=${r.offer_id}`);
    else if (!offer) navigate(`/agentur/anfragen/${d.inquiry.id}/angebot/${r.offer_id}`, { replace: true });
  }

  // K-538: „PDF-Vorschau“ öffnet das Vorschaufenster (vorher lud sie das PDF nur herunter)
  const [vorschauQuelle, setVorschauQuelle] = useState<PdfQuelle | null>(null);
  async function vorschau() {
    const p = pruefe();
    if (!p) return;
    setVorschauQuelle({ title: t("Vorschau: Angebot (Entwurf)", "Preview: offer (draft)"), laden: async () => {
      const fake: OfferRow = {
        ...(offer ?? ({} as OfferRow)), id: "vorschau", inquiry_id: d.inquiry.id, version: offer?.version ?? d.offers.length + 1, status: "draft", number: null, currency: cur,
        language: meta.language, valid_until: meta.validUntil, prices_basis: meta.basis, commission_included: meta.commissionIncluded,
        agency_fee_bp: p.values.agency_fee_bp as number | null, intro: meta.intro, terms: meta.terms, subtotal_cents: summen.subtotalCents, fee_cents: summen.feeCents,
        total_cents: summen.totalCents, issuer_snapshot: null, recipient_snapshot: null, finalized_at: null, sent_at: null, sent_to: null, sent_external: false,
        pdf_path: null, created_at: today, updated_at: today,
      };
      const lines: OfferLineRow[] = p.lines.map((l, i) => ({ ...l, id: `v${i}`, offer_id: "vorschau", amount_cents: 0, sort_order: l.sort_order ?? i }));
      const f = await angebotPdf({ ...d, lines }, fake, { talents, platformLabel: pl, orgName: ws.org.name, today, locale: ws.org.locale });
      return { blob: new Blob([f.bytes as BlobPart], { type: "application/pdf" }), filename: f.filename };
    } });
  }

  const entwurfHinweis = t("Entwurf: Preise und Zuschläge frei änderbar. Beim Fertigstellen bekommt das Angebot seine Nummer und bleibt danach unverändert; Änderungen gehen dann nur über eine neue Fassung.", "Draft: prices and surcharges can be changed freely. When finalised, the offer gets its number and remains unchanged afterwards.");
  return (
    <main id="main" className="content tk-koop tk-anfragen tk-formular tk-angebot-editor">
      <PdfVorschau quelle={vorschauQuelle} onClose={() => setVorschauQuelle(null)} />
      {rueckfrage.element}
      <div className="pagehead">
        <div>
          <div className="eyebrow"><Link to="/agentur/anfragen">{t("Anfragen", "Inquiries")}</Link> · <Link to={`/agentur/anfragen/${d.inquiry.id}`}>{d.inquiry.title}</Link></div>
          <h1>{offer ? t(`Angebot · Fassung ${offer.version}`, `Offer · version ${offer.version}`) : von ? t("Neue Fassung", "New version") : t("Angebot erstellen", "Create offer")}</h1>
          {/* Kompakt (D-1): Anfrage und Hinweis zum Entwurf in einer Zeile, der volle Hinweis im Tooltip */}
          <p title={entwurfHinweis}><Link to={`/agentur/anfragen/${d.inquiry.id}`}>{typeof d.brand?.name === "string" ? `${d.brand.name} · ` : ""}{d.inquiry.title}</Link> · {entwurfHinweis}</p>
        </div>
      </div>
      {hinweise.length > 0 && <div className="notice tk-koop-warnung" role="status"><ul>{hinweise.map((h) => <li key={h}>{h}</li>)}</ul></div>}

      <section className="panel stack">
        <h2>{t("Rahmen", "Framework")}</h2>
        <div className="grid tk-angebot-rahmen">
          <label>{t("Gültig bis", "Valid until")}<input type="date" value={meta.validUntil} onChange={(e) => setMeta({ ...meta, validUntil: e.target.value })} /></label>
          <label>
            {t("Sprache des Angebots", "Offer language")}<select value={meta.language} onChange={(e) => setMeta({ ...meta, language: e.target.value as "de" | "en" })}>
              <option value="de">{t("Deutsch", "German")}</option>
              <option value="en">{t("Englisch", "English")}</option>
            </select>
          </label>
          <label>
            {t("Preise", "Pricing")}<select value={meta.basis} onChange={(e) => setMeta({ ...meta, basis: e.target.value as "net" | "gross" })}>
              <option value="net">{t("netto", "net")}</option>
              <option value="gross">{t("brutto", "gross")}</option>
            </select>
          </label>
          <label>
            {t("Agenturgebühr im Angebot", "Agency fee in the offer")}<select value={meta.commissionIncluded ? "inkl" : "zzgl"} onChange={(e) => setMeta({ ...meta, commissionIncluded: e.target.value === "inkl" })}>
              <option value="inkl">{t("schon im Preis enthalten", "already included in the price")}</option>
              <option value="zzgl">{t("kommt obendrauf", "added on top")}</option>
            </select>
          </label>
          {!meta.commissionIncluded && <label>{t("Agenturgebühr in Prozent", "Agency fee in percent")}<input inputMode="decimal" value={meta.feePercent} onChange={(e) => setMeta({ ...meta, feePercent: e.target.value })} placeholder="z. B. 15" /></label>}
        </div>
        <p className="notice">{t("Im Angebot steht: ", "The offer states: ")}{priceBasisText({ basis: meta.basis, commissionIncluded: meta.commissionIncluded, agencyFeeBp: feeBp != null && !Number.isNaN(feeBp) ? feeBp : null,
          country: typeof d.org?.country === "string" ? d.org.country : null, smallBusiness: d.org?.small_business === true, lang: meta.language })}</p>
        <div className="grid tk-angebot-texte">
        <label>{t("Einleitung (optional)", "Introduction (optional)")}<textarea rows={3} maxLength={2000} value={meta.intro} onChange={(e) => setMeta({ ...meta, intro: e.target.value })} placeholder={t("z. B. Vielen Dank für Ihre Anfrage. Gern bieten wir an:", "e.g. Thank you for your inquiry. We are happy to offer:")} /></label>
        <label>{t("Bedingungen und Hinweise (optional)", "Terms and notes (optional)")}<textarea rows={3} maxLength={4000} value={meta.terms} onChange={(e) => setMeta({ ...meta, terms: e.target.value })} placeholder={t("z. B. Zahlungsziel 14 Tage, Freigabe innerhalb von 3 Werktagen", "e.g. payment term 14 days, approval within 3 working days")} /></label>
        </div>
      </section>

      {offeneCreator.map((c) => {
        const eigene = zeilen.filter((z) => z.candidateId === c.id);
        const card = d.cards.find((k) => k.talent_id === c.talent_id);
        const sub = summen.byCandidate[c.id] ?? 0;
        return (
          <section key={c.id} className="panel stack" aria-label={t(`Positionen ${nameOf(talents.find((t) => t.id === c.talent_id))}`, `Items ${nameOf(talents.find((t) => t.id === c.talent_id))}`)}>
            <div className="row between">
              <h2>{nameOf(talents.find((t) => t.id === c.talent_id))}</h2>
              <strong className="num">{money(sub, cur)}</strong>
            </div>
            {!card && <p className="source-note">{t("Für diesen Creator gibt es noch keine Preisliste (Creator-Profil · Preisliste). Preise bitte von Hand eintragen.", "There is no price list for this creator yet (creator profile · price list). Please enter prices by hand.")}</p>}
            {card && card.currency !== cur && <p className="source-note">{t("Preisliste in ", "Price list in ")}{card.currency}{t(", Angebot in ", ", offer in ")}{cur}{t(": Preise bitte von Hand eintragen.", ": please enter prices by hand.")}</p>}
            {eigene.length > 0 && (
              <div className="tablewrap tk-cardtable tk-koop-karten tk-koop-formtabelle tk-sticky-end">
                <table>
                  <caption className="sr-only">{t("Positionen", "Items")}</caption>
                  <thead><tr><th scope="col">{t("Leistung", "Service")}</th><th scope="col">{t("Titel", "Title")}</th><th scope="col">{t("Menge", "Quantity")}</th><th scope="col">{t("Einzelpreis", "Unit price")}</th><th scope="col">{t("Zuschläge", "Surcharges")}</th><th scope="col" className="money">{t("Betrag", "Amount")}</th><th scope="col"><span className="sr-only">{t("Aktion", "Action")}</span></th></tr></thead>
                  <tbody>
                    {eigene.map((z) => {
                      const betrag = zeilenBetrag(z);
                      const anzahl = [z.usageBp, z.exclusivityBp, z.rushBp, z.revisionBp].filter((x) => x.trim() && x.trim() !== "0").length;
                      return (
                        <tr key={z.key}>
                          <td data-label={t("Leistung", "Service")} className="tk-koop-zelle">
                            <div className="stack tk-gap-sm tk-anfrage-leistung">
                              <select aria-label={t("Plattform", "Platform")} value={z.platform} onChange={(e) => setZeile(z.key, { platform: e.target.value })}>
                                {plattformen.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                              </select>
                              <select aria-label="Format" value={z.format} onChange={(e) => setZeile(z.key, { format: e.target.value })}>
                                {FORMAT_CHOICES.map((f) => <option key={f} value={f}>{deliverableFormatLabel(f, null, currentLang())}</option>)}
                              </select>
                              {z.format === "other" && <input aria-label={t("Format benennen", "Name the format")} maxLength={60} value={z.formatLabel} onChange={(e) => setZeile(z.key, { formatLabel: e.target.value })} placeholder={t("Format benennen", "Name the format")} />}
                            </div>
                          </td>
                          <td data-label={t("Titel", "Title")}><input aria-label={t("Titel", "Title")} maxLength={120} value={z.title} onChange={(e) => setZeile(z.key, { title: e.target.value })} placeholder="optional" /></td>
                          <td data-label={t("Menge", "Quantity")} className="tk-angebot-menge"><input aria-label={t("Menge", "Quantity")} inputMode="numeric" value={z.quantity} aria-invalid={Number.isNaN(parseQuantityInput(z.quantity, { integer: true })) || undefined} onChange={(e) => setZeile(z.key, { quantity: e.target.value })} /><MengeErkannt text={z.quantity} integer /></td>
                          <td data-label={t("Einzelpreis", "Unit price")}>
                            <div className="stack tk-gap-sm">
                              <input aria-label={t("Einzelpreis", "Unit price")} inputMode="decimal" value={z.price} aria-invalid={betragUnlesbar(z.price) || undefined} onChange={(e) => setZeile(z.key, { price: e.target.value })} />
                              <BetragErkannt text={z.price} currency={cur} />
                              {card && card.currency === cur && (
                                <button type="button" className="quiet" onClick={() => { const n = preisAusListe(z, card, d.items, d.inquiry); if (n) setZeile(z.key, n); else setFehler([t(`Kein Preis für ${deliverableFormatLabel(z.format, z.formatLabel, currentLang())} auf ${pl(z.platform)} in der Preisliste.`, `No price for ${deliverableFormatLabel(z.format, z.formatLabel, currentLang())} on ${pl(z.platform)} in the price list.`)]); }}>
                                  {t("Aus Preisliste", "From price list")}</button>
                              )}
                            </div>
                          </td>
                          <td data-label={t("Zuschläge", "Surcharges")}>
                            <details className="tk-anfrage-zuschlaege">
                              <summary>{anzahl ? `${anzahl} ${anzahl === 1 ? t("Zuschlag", "surcharge") : t("Zuschläge", "Surcharges")}` : t("Keine", "None")}</summary>
                              <div className="stack tk-gap-sm">
                                <label>{t("Nutzungsrechte %", "Usage rights %")}<input inputMode="decimal" value={z.usageBp} onChange={(e) => setZeile(z.key, { usageBp: e.target.value })} /></label>
                                <label className="row tk-koop-wahl"><input type="checkbox" checked={z.usagePerMonth} onChange={(e) => setZeile(z.key, { usagePerMonth: e.target.checked })} /><span>{t("je Monat", "per month")}</span></label>
                                {z.usagePerMonth && <label>{t("Monate", "Months")}<input inputMode="numeric" value={z.usageMonths} onChange={(e) => setZeile(z.key, { usageMonths: e.target.value })} /></label>}
                                <label>{t("Exklusivität %", "Exclusivity %")}<input inputMode="decimal" value={z.exclusivityBp} onChange={(e) => setZeile(z.key, { exclusivityBp: e.target.value })} /></label>
                                <label className="row tk-koop-wahl"><input type="checkbox" checked={z.exclusivityPerMonth} onChange={(e) => setZeile(z.key, { exclusivityPerMonth: e.target.checked })} /><span>{t("je Monat", "per month")}</span></label>
                                {z.exclusivityPerMonth && <label>{t("Monate", "Months")}<input inputMode="numeric" value={z.exclusivityMonths} onChange={(e) => setZeile(z.key, { exclusivityMonths: e.target.value })} /></label>}
                                <label>{t("Eilzuschlag %", "Rush surcharge %")}<input inputMode="decimal" value={z.rushBp} onChange={(e) => setZeile(z.key, { rushBp: e.target.value })} /></label>
                                <label>{t("Korrekturschleife % je Schleife", "Revision round % per round")}<input inputMode="decimal" value={z.revisionBp} onChange={(e) => setZeile(z.key, { revisionBp: e.target.value })} /></label>
                                <label>{t("Zusätzliche Schleifen", "Additional rounds")}<input inputMode="numeric" value={z.revisions} onChange={(e) => setZeile(z.key, { revisions: e.target.value })} /></label>
                              </div>
                            </details>
                          </td>
                          <td data-label={t("Betrag", "Amount")} className="num">{betrag == null ? "–" : money(betrag, cur)}</td>
                          <td className="tk-koop-aktion"><button type="button" className="tk-zeile-entfernen" aria-label={t("Position entfernen", "Remove item")} title={t("Position entfernen", "Remove item")} onClick={() => setZeilen((zs) => zs.filter((x) => x.key !== z.key))}>✕</button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {c.status === "proposed" && (
              <div className="row">
                <button type="button" onClick={() => setZeilen((zs) => [...zs, leereZeile(c.id, d.inquiry.platforms[0] ?? "instagram")])}>{t("Position hinzufügen", "Add item")}</button>
                {card && card.currency === cur && (
                  <button type="button" onClick={() => {
                    const neu = vorbelegung({ ...d, candidates: [c] }).zeilen;
                    setZeilen((zs) => [...zs.filter((x) => x.candidateId !== c.id), ...neu]);
                  }}>{t("Aus Preisliste neu befüllen", "Refill from price list")}</button>
                )}
              </div>
            )}
            {c.status !== "proposed" && <p className="source-note">{t("Für diesen Creator ist schon entschieden; seine Positionen können nicht fertiggestellt werden.", "This creator has already been decided; their items cannot be finalised.")}</p>}
          </section>
        );
      })}

      <section className="panel stack tk-angebot-summen" aria-label={t("Summen", "Totals")}>
        {summen.feeCents > 0 && <div className="task"><span>{t("Zwischensumme", "Subtotal")}</span><span className="num">{money(summen.subtotalCents, cur)}</span></div>}
        {summen.feeCents > 0 && <div className="task"><span>{t("Agenturgebühr ", "Agency fee ")}{meta.feePercent} %</span><span className="num">{money(summen.feeCents, cur)}</span></div>}
        <div className="task"><strong>{t("Gesamtsumme ", "Total ")}{meta.basis === "net" ? "netto" : "brutto"}</strong><strong className="num">{money(summen.totalCents, cur)}</strong></div>
        {d.inquiry.budget_cents != null && <p className="source-note">{t("Budget der Marke laut Anfrage: ", "Brand budget according to the inquiry: ")}{money(d.inquiry.budget_cents, cur)}{summen.totalCents > d.inquiry.budget_cents ? t(" · das Angebot liegt darüber", " · the offer is above it") : ""}.</p>}
      </section>

      {(fehler.length > 0 || error) && <div className="notice tk-koop-warnung" role="alert"><ul>{fehler.map((x) => <li key={x}>{x}</li>)}{error && <li>{error}</li>}</ul></div>}
      {neuGesperrt && <TarifHinweis modul="module_pipeline" id="angebot-neu-tarif" was={t("Neue Angebote", "New offers")} />}
      <div className="row">
        <button className="primary" disabled={!!busy || neuGesperrt} aria-describedby={neuGesperrt ? "angebot-neu-tarif" : undefined} onClick={() => void speichern(false)}>{busy === "speichern" ? t("Wird gespeichert …", "Saving …") : t("Entwurf speichern", "Save draft")}</button>
        <button disabled={!!busy || neuGesperrt} aria-describedby={neuGesperrt ? "angebot-neu-tarif" : undefined} onClick={() => void speichern(true)}>{t("Fertigstellen und senden …", "Finalise and send …")}</button>
        <button disabled={!!busy} onClick={() => void vorschau()}>{t("PDF-Vorschau", "PDF preview")}</button>
        <Link className="btn" to={`/agentur/anfragen/${d.inquiry.id}`}>{t("Abbrechen", "Cancel")}</Link>
      </div>
    </main>
  );
}
